export const RELATIONSHIP_RUN_TERMINAL_STATUSES = Object.freeze([
  'completed',
  'completedWithErrors',
  'failed',
  'expired',
  'cancelled',
  'superseded',
]);

export const RELATIONSHIP_ATTENTION_CLASSIFICATION = 'attention';

export function relationshipAttentionFilter(relationshipRunId) {
  if (!relationshipRunId) return null;
  return {
    relationshipRunId,
    classification: RELATIONSHIP_ATTENTION_CLASSIFICATION,
  };
}

export const RELATIONSHIP_HEALTH_BUCKETS = Object.freeze([
  { key: 'linked', label: 'Connected', color: 'success' },
  { key: 'unresolved', label: 'No match', color: 'warning' },
  { key: 'staleResolutionConfig', label: 'Matching rules changed', color: 'warning' },
  { key: 'staleResolutionInput', label: 'Source value changed', color: 'warning' },
  { key: 'staleProjectionConfig', label: 'Field setup changed', color: 'warning' },
  { key: 'staleProjectionInput', label: 'Field value changed', color: 'warning' },
  { key: 'staleAnchorConfig', label: 'Connection rules changed', color: 'warning' },
  { key: 'staleAnchorInput', label: 'Connection value changed', color: 'warning' },
  { key: 'outOfApplicability', label: 'Not included', color: 'grey' },
  { key: 'detached', label: 'Disconnected', color: 'info' },
  { key: 'targetMissing', label: 'Related record missing', color: 'error' },
]);

export const RUN_STATUS_META = Object.freeze({
  awaitingConfirmation: { label: 'Awaiting confirmation', color: 'warning', icon: 'mdi-shield-check-outline' },
  queued: { label: 'Starting', color: 'info', icon: 'mdi-progress-clock' },
  running: { label: 'Running', color: 'info', icon: 'mdi-progress-clock' },
  completed: { label: 'Completed', color: 'success', icon: 'mdi-check-circle-outline' },
  completedWithErrors: { label: 'Completed with errors', color: 'warning', icon: 'mdi-alert-circle-outline' },
  failed: { label: 'Failed', color: 'error', icon: 'mdi-close-circle-outline' },
  expired: { label: 'Expired', color: 'grey', icon: 'mdi-clock-alert-outline' },
  cancelled: { label: 'Cancelled', color: 'grey', icon: 'mdi-cancel' },
  superseded: { label: 'Superseded', color: 'grey', icon: 'mdi-swap-horizontal' },
});

export function runStatusMeta(status) {
  return RUN_STATUS_META[status] || {
    label: 'Status unavailable',
    color: 'grey',
    icon: 'mdi-help-circle-outline',
  };
}

// Outcome counters are only written by a run that actually executed. Preview, cancelled, expired,
// and superseded runs leave them at zero, so rendering them would claim a result that never happened.
export const RELATIONSHIP_RUN_EXECUTED_STATUSES = Object.freeze([
  'completed',
  'completedWithErrors',
  'failed',
]);

// These runs still hold a live plan, so the planned count is the only honest number to show.
export const RELATIONSHIP_RUN_PENDING_STATUSES = Object.freeze([
  'awaitingConfirmation',
  'queued',
  'running',
]);

const RUN_OUTCOME_BUCKETS = Object.freeze([
  { key: 'succeededCount', label: 'applied', color: 'success' },
  { key: 'failedCount', label: 'failed', color: 'error' },
  { key: 'driftedCount', label: 'changed since preview', color: 'warning' },
  { key: 'fieldErrorCount', label: 'field errors', color: 'warning' },
]);

// One presentation of what a run did, shared by the relationship card and the run history list.
export function relationshipRunCountEntries(run) {
  if (RELATIONSHIP_RUN_EXECUTED_STATUSES.includes(run?.status)) {
    return RUN_OUTCOME_BUCKETS
      .map((bucket) => ({ ...bucket, count: Number(run[bucket.key] || 0) }))
      .filter((bucket) => bucket.count > 0);
  }
  if (RELATIONSHIP_RUN_PENDING_STATUSES.includes(run?.status)) {
    return [{
      key: 'plannedActionCount',
      label: 'will change',
      color: 'primary',
      count: Number(run.plannedActionCount || 0),
    }];
  }
  // Cancelled, expired, and superseded runs never applied their plan, so every counter stayed zero.
  return [];
}

// RelationshipRunScopeKind. The first three reuse the run dialog's own scope wording.
export const RUN_SCOPE_LABELS = Object.freeze({
  rows: 'Selected rows',
  register: 'All applicable rows',
  filtered: 'Filtered rows',
  import: 'Rows from an import',
  reconcile: 'Reconcile',
  cleanup: 'Cleanup',
});

export function relationshipRunScopeLabel(run) {
  return RUN_SCOPE_LABELS[run?.scope?.kind] || 'Scope unavailable';
}

// RelationshipRunTrigger. Manual is the norm and stays unlabelled; an automatic run is the fact
// worth surfacing, because nobody in the room started it.
export const RUN_TRIGGER_LABELS = Object.freeze({
  import: 'Started by an import',
  schedule: 'Started on a schedule',
});

export function relationshipRunTriggerLabel(run) {
  return RUN_TRIGGER_LABELS[run?.trigger] || '';
}

export function formatRelationshipTimestamp(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// health.state reports source coverage only - see RelationshipHttpReadService.SourceFreshnessState.
// The eleven health buckets are not derived yet and always arrive as zero, so coverage is the only
// health signal the card can state as fact.
export const RELATIONSHIP_COVERAGE_LABELS = Object.freeze({
  sourceCurrent: 'Register data covered by the last run',
  sourceChanged: 'Register data changed since the last full run',
  sourcePartial: 'The last run covered part of the register',
  sourceNotRun: 'No run has covered the register yet',
});

export function relationshipCoverageLabel(relationship) {
  return RELATIONSHIP_COVERAGE_LABELS[relationship?.health?.state] || '';
}

export function registerOwnershipScope(register) {
  const groupId = register?.facilityGroupId || register?.facilityGroup?.id;
  const facilityId = register?.facilityId || register?.facility?.id;

  if (groupId && facilityId) {
    return {
      kind: null,
      id: null,
      label: 'Invalid dual ownership',
      invalid: true,
    };
  }

  if (groupId) {
    return {
      kind: 'facilityGroup',
      id: groupId,
      label: register?.facilityGroupName || register?.facilityGroup?.name || 'Facility Group',
    };
  }

  if (facilityId) {
    return {
      kind: 'facility',
      id: facilityId,
      label: register?.facilityName || register?.facility?.name || 'Facility',
    };
  }

  return { kind: null, id: null, label: 'No ownership scope', invalid: false };
}

export function capabilityScopeDescriptor(capability, scopeKind) {
  const scopes = capability?.ownershipScopes || [];
  return scopes.find((scope) => typeof scope === 'object' && scope?.kind === scopeKind) || null;
}

export function capabilitySupportsScope(capability, scopeKind, operation = 'resolve') {
  if (!capability || capability.isEnabled !== true) return false;
  const scope = capabilityScopeDescriptor(capability, scopeKind);
  if (!scope) return false;

  const operationFlag = {
    resolve: 'canResolve',
    create: 'canCreate',
    edit: 'canEdit',
    renderPoi: 'canRenderPoi',
  }[operation];

  return operationFlag ? scope[operationFlag] === true : false;
}

export function capabilityLabel(capability) {
  return safeOperatorLabel(capability?.displayName || capability?.targetKind, 'Related record type');
}

export function capabilityCanCreate(capability, scopeKind) {
  return capabilitySupportsScope(capability, scopeKind, 'create');
}

export function capabilityCanRenderPoi(capability, scopeKind) {
  return capabilitySupportsScope(capability, scopeKind, 'renderPoi');
}

export function capabilityOperationSummary(capability, scopeKind) {
  const scope = capabilityScopeDescriptor(capability, scopeKind);
  if (!scope || scope.canResolve !== true) return [];

  const operations = [{ key: 'resolve', label: 'Link existing', color: 'primary' }];
  if (scope.canCreate === true) operations.push({ key: 'create', label: 'Create missing', color: 'success' });
  if (scope.canRenderPoi === true) operations.push({ key: 'poi', label: '3D display', color: 'info' });
  return operations;
}

export const RELATIONSHIP_CREATE_WRITE_MODE = 'create';

const emptyDestinationSchemaAnalysis = (issue = null) => ({
  valid: false,
  isWritable: false,
  issue,
  groups: [],
  options: [],
  dynamicDefinitionGroups: [],
});

const requiredString = (value) => typeof value === 'string' && value.trim().length > 0;
const stringList = (value) => Array.isArray(value) && value.every(requiredString);

// Native and link destinations are both storeless: no store key, no group-level write modes, and no
// new keys. They differ only in what the mapped cell means - a link cell names another record
// instead of carrying a value - so they share every structural rule below.
const STORELESS_NAMESPACES = Object.freeze(['native', 'link']);
const DESTINATION_NAMESPACES = Object.freeze(['native', 'custom', 'link']);

// The display namespace every normalized option carries. The picker groups, labels, and colours by
// these exact strings, so they live here rather than being retyped at each call site.
export const DESTINATION_NAMESPACE_LABELS = Object.freeze({
  native: 'Native field',
  link: 'Link field',
  definedCustomField: 'Defined custom field',
  dynamicAttribute: 'Custom attribute',
});

function groupIssue(index, detail) {
  return `Destination group ${index + 1} is inconsistent: ${detail}`;
}

function destinationIssue(groupIndex, itemIndex, detail) {
  return `Destination ${itemIndex + 1} in group ${groupIndex + 1} is inconsistent: ${detail}`;
}

function normalizeGroup(group, index) {
  if (!group || typeof group !== 'object' || Array.isArray(group)) {
    return { issue: groupIssue(index, 'the group object is missing.') };
  }
  if (!DESTINATION_NAMESPACES.includes(group.namespace)) {
    return { issue: groupIssue(index, 'namespace must be native, link, or custom.') };
  }
  if (!['native', 'dynamic', 'definitionBound'].includes(group.mode)) {
    return { issue: groupIssue(index, 'store mode is not supported.') };
  }
  if (typeof group.allowNewKeys !== 'boolean'
    || !Object.prototype.hasOwnProperty.call(group, 'storeKey')
    || !Object.prototype.hasOwnProperty.call(group, 'mergeSemantics')
    || !stringList(group.valueTypes)
    || !stringList(group.writeModes)
    || !Array.isArray(group.items)) {
    return { issue: groupIssue(index, 'required store metadata is missing.') };
  }

  const isStoreless = STORELESS_NAMESPACES.includes(group.namespace);
  if (isStoreless && (group.mode !== 'native'
    || group.storeKey !== null
    || group.allowNewKeys
    || group.writeModes.length)) {
    return { issue: groupIssue(index, 'the native and link namespaces cannot declare a store, custom mode, new keys, or group write modes.') };
  }
  if (!isStoreless && (!requiredString(group.storeKey) || group.mode === 'native')) {
    return { issue: groupIssue(index, 'a custom namespace requires a store key and custom store mode.') };
  }
  if (!isStoreless && group.mode === 'definitionBound' && group.allowNewKeys) {
    return { issue: groupIssue(index, 'definition-bound stores cannot advertise arbitrary new keys.') };
  }

  return {
    value: {
      namespace: group.namespace,
      storeKey: isStoreless ? null : group.storeKey,
      mode: group.mode,
      allowNewKeys: group.allowNewKeys,
      valueTypes: [...group.valueTypes],
      writeModes: [...group.writeModes],
      mergeSemantics: group.mergeSemantics ?? null,
      items: [],
    },
  };
}

function normalizeDestination(item, group, groupIndex, itemIndex) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return { issue: destinationIssue(groupIndex, itemIndex, 'the item object is missing.') };
  }
  if (!requiredString(item.label)
    || !requiredString(item.type)
    || (item.category !== null && item.category !== undefined && !requiredString(item.category))
    || typeof item.required !== 'boolean'
    || typeof item.identity !== 'boolean'
    || typeof item.mapWritable !== 'boolean'
    || !stringList(item.writeModes)
    || !stringList(item.allowedTransforms)) {
    return { issue: destinationIssue(groupIndex, itemIndex, 'required field metadata is missing.') };
  }
  if ((item.mapWritable && !item.writeModes.length)
    || (!item.mapWritable && item.writeModes.length)
    || (item.identity && item.mapWritable)) {
    return { issue: destinationIssue(groupIndex, itemIndex, 'identity, map-writable, and write-mode flags contradict each other.') };
  }
  if (group.namespace === 'custom'
    && item.writeModes.some((mode) => !group.writeModes.includes(mode))) {
    return { issue: destinationIssue(groupIndex, itemIndex, 'item write modes are not allowed by its custom store group.') };
  }

  const descriptor = item?.destination;
  const kind = descriptor?.kind;
  const isNative = kind === 'native';
  const isLink = kind === 'link';
  const isStoreless = isNative || isLink;
  const namespace = isNative
    ? DESTINATION_NAMESPACE_LABELS.native
    : isLink
      ? DESTINATION_NAMESPACE_LABELS.link
      : kind === 'definedCustomField'
        ? DESTINATION_NAMESPACE_LABELS.definedCustomField
        : DESTINATION_NAMESPACE_LABELS.dynamicAttribute;
  const key = descriptor?.fieldKey || descriptor?.definitionRef || descriptor?.definitionId;
  const label = item.label;
  const category = requiredString(item.category) ? item.category.trim() : null;
  if (!kind || !key) {
    return { issue: destinationIssue(groupIndex, itemIndex, 'the stable destination identity is missing.') };
  }

  let destination;
  if (isNative) {
    const fieldKey = descriptor.fieldKey;
    if (group.namespace !== 'native' || group.mode !== 'native' || !requiredString(fieldKey)) {
      return { issue: destinationIssue(groupIndex, itemIndex, 'a native destination was returned under the wrong namespace or without a stable field key.') };
    }
    destination = { kind: 'native', fieldKey };
  } else if (isLink) {
    // A link destination carries nothing but its field key: the value it writes is resolved by the
    // server while compiling the preview, so there is no store or snapshot to keep in step.
    const fieldKey = descriptor.fieldKey;
    if (group.namespace !== 'link' || group.mode !== 'native' || !requiredString(fieldKey)) {
      return { issue: destinationIssue(groupIndex, itemIndex, 'a link destination was returned under the wrong namespace or without a stable field key.') };
    }
    destination = { kind: 'link', fieldKey };
  } else if (kind === 'definedCustomField') {
    const definitionId = descriptor.definitionId;
    if (group.namespace !== 'custom'
      || group.mode !== 'definitionBound'
      || descriptor.storeKey !== group.storeKey
      || !requiredString(definitionId)
      || !requiredString(descriptor.labelSnapshot)) {
      return { issue: destinationIssue(groupIndex, itemIndex, 'the definition-bound identity does not match its custom store group.') };
    }
    destination = {
      kind: 'definedCustomField',
      storeKey: descriptor.storeKey,
      definitionId,
      labelSnapshot: descriptor.labelSnapshot,
    };
  } else if (kind === 'dynamicAttribute') {
    const definitionRef = descriptor.definitionRef;
    const attributeKeySnapshot = descriptor.attributeKeySnapshot;
    if (group.namespace !== 'custom'
      || group.mode !== 'dynamic'
      || descriptor.storeKey !== group.storeKey
      || !requiredString(definitionRef)
      || !requiredString(attributeKeySnapshot)) {
      return { issue: destinationIssue(groupIndex, itemIndex, 'the dynamic-attribute identity does not match its custom store group.') };
    }
    destination = {
      kind: 'dynamicAttribute',
      storeKey: descriptor.storeKey,
      definitionRef,
      attributeKeySnapshot,
    };
  } else {
    return { issue: destinationIssue(groupIndex, itemIndex, 'the destination kind is not supported.') };
  }

  const canonicalKey = `${kind}:${descriptor.storeKey || ''}:${key}`;
  const identityHint = isStoreless
    ? `${key} - ${kind}`
    : kind === 'dynamicAttribute'
      ? `${descriptor.storeKey} - dynamic - ${descriptor.attributeKeySnapshot}`
      : `${descriptor.storeKey} - definition-bound - ${descriptor.definitionId}`;
  const groupMetadata = {
    namespace: group.namespace,
    storeKey: group.storeKey,
    mode: group.mode,
    allowNewKeys: group.allowNewKeys,
    valueTypes: [...group.valueTypes],
    writeModes: [...group.writeModes],
    mergeSemantics: group.mergeSemantics,
  };
  const option = {
    ...item,
    category,
    key: canonicalKey,
    text: `${label} (${namespace.toLowerCase()}) - ${isStoreless ? key : `${descriptor.storeKey} / ${key}`}`,
    label,
    namespace,
    identityHint,
    fieldKey: descriptor.fieldKey || null,
    storeKey: descriptor.storeKey || null,
    definitionRef: descriptor.definitionRef || null,
    definitionId: descriptor.definitionId || null,
    attributeKeySnapshot: descriptor.attributeKeySnapshot || null,
    labelSnapshot: descriptor.labelSnapshot || null,
    destinationGroup: groupMetadata,
    destination,
  };

  // A storeless group never advertises group-level write modes - the contract forbids it - so its
  // items carry the only write modes there are. Reading the group would drop every native and link
  // destination from the picker.
  const groupAllowsCreate = STORELESS_NAMESPACES.includes(group.namespace)
    || group.writeModes.includes(RELATIONSHIP_CREATE_WRITE_MODE);
  return {
    canonicalKey,
    value: {
      ...item,
      category,
      destination,
      destinationGroup: groupMetadata,
    },
    option: item.mapWritable === true
      && item.identity !== true
      && item.writeModes.includes(RELATIONSHIP_CREATE_WRITE_MODE)
      && groupAllowsCreate
      ? option
      : null,
  };
}

export function analyzeDestinationSchema(schema, expected = {}) {
  if (!schema) return emptyDestinationSchemaAnalysis();
  if (typeof schema !== 'object' || Array.isArray(schema)) {
    return emptyDestinationSchemaAnalysis('The target schema response is not an object.');
  }
  if (!requiredString(schema.targetKind)
    || !['facility', 'facilityGroup'].includes(schema.targetScopeKind)
    || !requiredString(schema.targetScopeId)
    || !requiredString(schema.schemaToken)
    || typeof schema.isWritable !== 'boolean'
    || !Object.prototype.hasOwnProperty.call(schema, 'targetContext')
    || !Array.isArray(schema.destinationGroups)) {
    return emptyDestinationSchemaAnalysis('The target schema response is missing exact-scope metadata.');
  }
  if (expected.targetKind && schema.targetKind !== expected.targetKind) {
    return emptyDestinationSchemaAnalysis('The resolved target type does not match the selected target.');
  }
  if (expected.scopeKind && schema.targetScopeKind !== expected.scopeKind) {
    return emptyDestinationSchemaAnalysis('The resolved ownership kind does not match this register.');
  }
  if (expected.scopeId && String(schema.targetScopeId) !== String(expected.scopeId)) {
    return emptyDestinationSchemaAnalysis('The resolved owner does not match this register.');
  }

  const groupKeys = new Set();
  const destinationKeys = new Set();
  const groups = [];
  const options = [];
  for (let groupIndex = 0; groupIndex < schema.destinationGroups.length; groupIndex += 1) {
    const sourceGroup = schema.destinationGroups[groupIndex];
    const result = normalizeGroup(sourceGroup, groupIndex);
    if (result.issue) return emptyDestinationSchemaAnalysis(result.issue);
    const group = result.value;
    if (!schema.isWritable && (group.allowNewKeys || group.writeModes.length)) {
      return emptyDestinationSchemaAnalysis(groupIssue(groupIndex, 'a read-only target scope cannot advertise new keys or group write modes.'));
    }
    const groupKey = `${group.namespace}:${group.storeKey || ''}`;
    if (groupKeys.has(groupKey)) {
      return emptyDestinationSchemaAnalysis(groupIssue(groupIndex, 'the namespace and store are duplicated.'));
    }
    groupKeys.add(groupKey);

    for (let itemIndex = 0; itemIndex < sourceGroup.items.length; itemIndex += 1) {
      const sourceItem = sourceGroup.items[itemIndex];
      if (!schema.isWritable && (sourceItem?.mapWritable === true || sourceItem?.writeModes?.length > 0)) {
        return emptyDestinationSchemaAnalysis(destinationIssue(groupIndex, itemIndex, 'a read-only target scope cannot advertise mapping writes.'));
      }
      const itemResult = normalizeDestination(
        sourceItem,
        group,
        groupIndex,
        itemIndex,
      );
      if (itemResult.issue) return emptyDestinationSchemaAnalysis(itemResult.issue);
      if (destinationKeys.has(itemResult.canonicalKey)) {
        return emptyDestinationSchemaAnalysis(destinationIssue(groupIndex, itemIndex, 'the stable destination identity is duplicated.'));
      }
      destinationKeys.add(itemResult.canonicalKey);
      group.items.push(itemResult.value);
      if (itemResult.option) options.push(itemResult.option);
    }
    groups.push(group);
  }

  return {
    valid: true,
    isWritable: schema.isWritable,
    issue: null,
    groups,
    options,
    dynamicDefinitionGroups: groups.filter((group) => group.namespace === 'custom'
      && group.mode === 'dynamic'
      && group.allowNewKeys === true
      && group.writeModes.includes(RELATIONSHIP_CREATE_WRITE_MODE)),
  };
}

export function destinationOptions(schema, expected = {}) {
  return analyzeDestinationSchema(schema, expected).options;
}

export const RELATIONSHIP_LINK_DESTINATION_KIND = 'link';

export function relationshipHealthEntries(relationship) {
  const health = relationship?.health || {};
  return RELATIONSHIP_HEALTH_BUCKETS
    .map((bucket) => ({ ...bucket, count: Number(health[bucket.key] || 0) }))
    .filter((bucket) => bucket.count > 0);
}

// Run history arrives newest first (RelationshipHttpRunReadService orders by CreateDate desc), so
// the first match is the relationship's latest run.
export function latestRelationshipRun(runHistory, relationshipId) {
  if (!Array.isArray(runHistory) || !relationshipId) return null;
  return runHistory.find((run) => run?.relationshipId === relationshipId) || null;
}

export function latestExecutedRelationshipRun(runHistory, relationshipId) {
  if (!Array.isArray(runHistory) || !relationshipId) return null;
  return runHistory.find((run) => run?.relationshipId === relationshipId
    && RELATIONSHIP_RUN_EXECUTED_STATUSES.includes(run?.status)) || null;
}

// The problem buckets are exactly the outcome buckets the shared count derivation already marks as
// error or warning, so the two can never drift apart. A pending run contributes nothing: its only
// entry is the planned count, which is not a problem.
export function relationshipRunAttentionCount(run) {
  return relationshipRunCountEntries(run)
    .filter((entry) => ['error', 'warning'].includes(entry.color))
    .reduce((total, entry) => total + entry.count, 0);
}

// Work the latest executed run could not complete, summed across relationships.
//
// Run history is paged (20 newest runs for the whole register, RelationshipPagingContract), so a
// relationship whose last executed run falls outside that window is EXCLUDED rather than counted
// as zero - an unloaded run is unknown, not clean. `covered` reports how many relationships the
// number actually speaks for so the caller can say so out loud.
export function relationshipAttentionSummary(relationships, runHistory) {
  const list = Array.isArray(relationships) ? relationships : [];
  return list.reduce((summary, relationship) => {
    const run = latestExecutedRelationshipRun(runHistory, relationship?.id);
    if (!run) return summary;
    return {
      items: summary.items + relationshipRunAttentionCount(run),
      covered: summary.covered + 1,
    };
  }, { items: 0, covered: 0 });
}

export function relationshipRunNeedsAttention(run) {
  if (!run) return false;
  if (['failed', 'completedWithErrors'].includes(run.status)) return true;
  if (!RELATIONSHIP_RUN_EXECUTED_STATUSES.includes(run.status)) return false;
  return Number(run.failedCount || 0)
    + Number(run.driftedCount || 0)
    + Number(run.fieldErrorCount || 0) > 0;
}

// The eleven health buckets are never populated by the API, so relationship-side signals alone
// could not see a run that failed. The latest run supplies what the buckets cannot. Both the tab
// and the workspace rail call this one predicate so their counts cannot disagree.
export function relationshipNeedsAttention(relationship, runHistory = []) {
  if (!relationship) return false;
  if (relationship.attributionStatus === 'quarantine' || relationship.state === 'error') return true;
  if (relationshipHealthEntries(relationship)
    .some((entry) => ['error', 'warning'].includes(entry.color))) return true;
  return relationshipRunNeedsAttention(latestRelationshipRun(runHistory, relationship.id));
}

// The display name a register column shows in the Data grid. The workspace loads the schema
// best-effort, so a missing schema still yields a presentable label instead of a stored key.
export function registerColumnLabel(registerSchema, fieldKey) {
  const columns = registerSchema?.columns || registerSchema?.Columns || [];
  const column = columns.find((item) => (item.name || item.Name) === fieldKey);
  return column?.displayName || column?.DisplayName || safeOperatorLabel(fieldKey, 'Source field');
}

export function relationshipSourceLabel(relationship) {
  return safeOperatorLabel(
    relationship?.sourceFieldDisplayName || relationship?.sourceFieldKey,
    'Source field',
  );
}

export function relationshipTargetLabel(relationship) {
  return safeOperatorLabel(
    relationship?.targetDisplayName || relationship?.targetKind,
    'Related record',
  );
}

function safeOperatorLabel(value, fallback) {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  const text = value.trim();
  if (/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(text)) return fallback;
  const words = text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : fallback;
}
